import axios from 'axios';
import { logger } from '@parrot-software/observability';

import type { ITipPort } from '@onboarding/application/ports';
import type { TipDto } from '@onboarding/application/dtos';
import { createTipConfig } from '@onboarding/domain/value-objects';

const onbLog = logger.forDomain('ONB');

/**
 * Use case for applying tip configuration.
 *
 * Flow: TipDto → domain value object → persist pay config (Pay API).
 * Only toggles `isTippingEnabled` on the payment terminal configuration.
 * Tip retention by role is a separate concern handled in /settings/tips.
 *
 * @param props.tipPort - ITipPort implementation (injected)
 * @returns Use case executor
 */
export const applyTipUseCase = (props: { tipPort: ITipPort }) => {
  const { tipPort } = props;

  const execute = async (dto: TipDto): Promise<void> => {
    try {
      const config = createTipConfig(dto.enabled);
      await tipPort.applyPayConfig(config);
    } catch (error) {
      onbLog.error('tip_apply_failed', {
        'error.kind': (error as { constructor?: { name?: string } })?.constructor?.name ?? 'UnknownError',
        'error.message': error instanceof Error ? error.message : String(error),
        'http.status_code': axios.isAxiosError(error) ? error.response?.status : undefined,
        'onb.tip_enabled': dto.enabled,
      });
      throw error;
    }
  };

  return { execute };
};
